import type { CanonicalEvidenceCitation, CitationSupportStatus } from '@aide/platform-types'

export type AgentProtocolVersion = 'v1'

export interface AgentProgressEvent {
  type: 'event'
  protocol_version: AgentProtocolVersion
  request_id: string
  session_id?: string
  event_type: string
  ts_ms: number
  payload: Record<string, unknown>
}

export interface AgentAckFrame {
  type: 'ack'
  request_id: string
}

export interface AgentFinalFrame {
  type: 'final'
  protocol_version: AgentProtocolVersion
  request_id: string
  session_id: string
  response: string
  response_format?: 'markdown' | 'plain' | string
  render_hints?: Record<string, unknown>
  tool_calls?: unknown[]
  iterations?: number
  tokens_used?: number
  duration_ms?: number
  budget_usage?: Record<string, unknown>
  citations: CanonicalEvidenceCitation[]
  context_bundle?: {
    file_refs: { repo: string; path: string }[]
    knowledge_refs: string[]
    external_refs: { id: string; source: string; title: string }[]
    query: string
  } | null
}

export interface AgentErrorFrame {
  type: 'error'
  protocol_version?: AgentProtocolVersion
  request_id?: string
  detail: string
}

export interface AgentUpdatesFrame {
  type: 'updates'
  protocol_version: AgentProtocolVersion
  request_id: string
  events: AgentProgressEvent[]
}

export interface AgentPongFrame {
  type: 'pong'
}

export interface AgentConfirmationRequiredFrame {
  type: 'confirmation_required'
  protocol_version: AgentProtocolVersion
  request_id: string
  confirmation_id: string
  tool_name: string
  tool_arguments: Record<string, unknown>
  description: string
  budget_status: Record<string, unknown>
}

export type ConfirmationAction = 'approve' | 'approve_extend' | 'deny' | 'deny_stop'

export interface ConfirmationResponsePayload {
  type: 'confirmation_response'
  request_id: string
  confirmation_id: string
  action: ConfirmationAction
  extend?: {
    additional_time_ms?: number
    additional_tool_calls?: number
  }
}

export type AgentServerFrame =
  | AgentProgressEvent
  | AgentAckFrame
  | AgentFinalFrame
  | AgentErrorFrame
  | AgentUpdatesFrame
  | AgentPongFrame
  | AgentConfirmationRequiredFrame

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function citationString(value: Record<string, unknown>, snake: string, camel: string): string {
  return String(value[snake] ?? value[camel] ?? '')
}

function normalizeCitation(value: unknown): CanonicalEvidenceCitation | null {
  if (!isRecord(value)) return null
  const citationId = citationString(value, 'citation_id', 'citationId')
  const evidenceRef = citationString(value, 'evidence_ref', 'evidenceRef')
  const sourceSystem = citationString(value, 'source_system', 'sourceSystem')
  const sourceType = citationString(value, 'source_type', 'sourceType')
  const locator = String(value.locator ?? '')
  const storageMode = citationString(value, 'storage_mode', 'storageMode')
  const supportStatus = citationString(value, 'support_status', 'supportStatus') as CitationSupportStatus
  const reason = String(value.reason ?? '')

  if (!citationId || !evidenceRef || !sourceSystem || !sourceType || !locator || !storageMode || !supportStatus) {
    return null
  }

  return {
    citationId,
    evidenceRef,
    sourceSystem,
    sourceType,
    locator,
    storageMode,
    contentHash: (value.content_hash ?? value.contentHash ?? null) as string | null,
    supportStatus,
    reason,
  }
}

function normalizeCitations(value: unknown): CanonicalEvidenceCitation[] {
  return Array.isArray(value)
    ? value.map(normalizeCitation).filter((citation): citation is CanonicalEvidenceCitation => citation !== null)
    : []
}

function isProgressEvent(value: unknown): value is AgentProgressEvent {
  if (!isRecord(value)) return false
  return (
    value.type === 'event' &&
    value.protocol_version === 'v1' &&
    !!asString(value.request_id) &&
    !!asString(value.event_type) &&
    typeof value.ts_ms === 'number' &&
    isRecord(value.payload)
  )
}

export function parseAgentServerFrame(value: unknown): AgentServerFrame | null {
  if (!isRecord(value)) return null
  const type = value.type
  if (type === 'pong') return { type: 'pong' }
  if (type === 'ack' && asString(value.request_id)) {
    return { type: 'ack', request_id: String(value.request_id) }
  }
  if (type === 'event' && isProgressEvent(value)) {
    return value
  }
  if (
    type === 'final' &&
    value.protocol_version === 'v1' &&
    asString(value.request_id) &&
    asString(value.session_id) &&
    typeof value.response === 'string'
  ) {
    return {
      type: 'final',
      protocol_version: 'v1',
      request_id: String(value.request_id),
      session_id: String(value.session_id),
      response: String(value.response),
      response_format: typeof value.response_format === 'string' ? value.response_format : 'markdown',
      render_hints: isRecord(value.render_hints) ? value.render_hints : {},
      tool_calls: Array.isArray(value.tool_calls) ? value.tool_calls : [],
      iterations: typeof value.iterations === 'number' ? value.iterations : undefined,
      tokens_used: typeof value.tokens_used === 'number' ? value.tokens_used : undefined,
      duration_ms: typeof value.duration_ms === 'number' ? value.duration_ms : undefined,
      budget_usage: isRecord(value.budget_usage) ? value.budget_usage : undefined,
      citations: normalizeCitations(value.citations),
      context_bundle: isRecord(value.context_bundle) ? value.context_bundle as AgentFinalFrame['context_bundle'] : null,
    }
  }
  if (type === 'error' && typeof value.detail === 'string') {
    return {
      type: 'error',
      protocol_version: value.protocol_version === 'v1' ? 'v1' : undefined,
      request_id: typeof value.request_id === 'string' ? value.request_id : undefined,
      detail: value.detail
    }
  }
  if (
    type === 'updates' &&
    value.protocol_version === 'v1' &&
    asString(value.request_id) &&
    Array.isArray(value.events) &&
    value.events.every(isProgressEvent)
  ) {
    return {
      type: 'updates',
      protocol_version: 'v1',
      request_id: String(value.request_id),
      events: value.events
    }
  }
  if (
    type === 'confirmation_required' &&
    value.protocol_version === 'v1' &&
    asString(value.request_id) &&
    asString(value.confirmation_id) &&
    asString(value.tool_name)
  ) {
    return {
      type: 'confirmation_required',
      protocol_version: 'v1',
      request_id: String(value.request_id),
      confirmation_id: String(value.confirmation_id),
      tool_name: String(value.tool_name),
      tool_arguments: isRecord(value.tool_arguments) ? value.tool_arguments : {},
      description: typeof value.description === 'string' ? value.description : '',
      budget_status: isRecord(value.budget_status) ? value.budget_status : {},
    }
  }
  return null
}
